/*
 *  (c) 2015 - 2025 ENISCO by FORCAM GmbH
 */

import React, {SyntheticEvent} from 'react'
import {Box, TextField} from '@mui/material'
import NotificationTab from './NotificationTab'
import {
  clone,
  SeverityConfiguration,
  SeverityType,
  useNotificationConfiguration,
  useNotificationPosition,
  useUserName
} from '@enisco/mcc-react-lib'
import useProfile from '../../../../hooks/useProfile'
import {SxProps} from '@mui/system'
import {defineMessage, FormattedMessage, MessageDescriptor, useIntl} from 'react-intl'
import Autocomplete from '@mui/material/Autocomplete'
import {NotificationPositionType} from '@enisco/mcc-react-lib/dist/types/context/model/notification'
import useProfileNotificationPosition from '../../../../hooks/useProfileNotificationPosition'

interface NotificationPositionOption {
  position: NotificationPositionType
  description: MessageDescriptor
}

const options : NotificationPositionOption[] = [
  {position: 'top-left', description: defineMessage({id:'NotificationPosition.TopLeft', defaultMessage: 'Top-Left'})},
  {position: 'top-center', description: defineMessage({id:'NotificationPosition.TopCenter', defaultMessage: 'Top-Center'})},
  {position: 'top-right', description: defineMessage({id:'NotificationPosition.TopRight', defaultMessage: 'Top-Right'})},
  {position: 'bottom-left', description: defineMessage({id:'NotificationPosition.BottomLeft', defaultMessage: 'Bottom-Left'})},
  {position: 'bottom-center', description: defineMessage({id:'NotificationPosition.BottomCenter', defaultMessage: 'Bottom-Center'})},
  {position: 'bottom-right', description: defineMessage({id:'NotificationPosition.BottomRight', defaultMessage: 'Bottom-Right'})},
]
const SnackBarTab: React.FC<{sx?: SxProps}> = ({sx}) => {
  const [, setProfile] = useProfile()
  const [notificationConfiguration, setNotificationConfiguration] =
    useNotificationConfiguration()
  const [, setNotificationPosition] = useNotificationPosition()
  const [notificationPosition] = useProfileNotificationPosition()
  const currentPosition = options.find((o) => o.position === notificationPosition) ?? 'top-right'
  const userName = useUserName()
  const intl = useIntl()

  const saveProfile = (
    configuration: SeverityConfiguration,
    type: SeverityType
  ) => {
    const updateConfigs = clone(notificationConfiguration)
    switch (type) {
      case 'error':
        updateConfigs.error = configuration
        break
      case 'warning':
        updateConfigs.warning = configuration
        break
      case 'success':
        updateConfigs.success = configuration
        break
      case 'info':
        updateConfigs.info = configuration
        break
    }
    setNotificationConfiguration(updateConfigs)
    setProfile({notifications: updateConfigs})
  }

  const savePosition = (e: SyntheticEvent, value: NotificationPositionOption | null) => {
    const newPosition: NotificationPositionType = value?.position ?? 'top-right'
    if (userName) {
      setNotificationPosition(newPosition)
      setProfile({notification_position: newPosition})
    }
  }

  return (
    <Box flexDirection={'column'} sx={sx}>
      <Autocomplete
        disabled={userName === undefined}
        sx={{width: '50%'}}
        value={currentPosition as NotificationPositionOption}
        onChange={savePosition}
        id='change-notificationPosition'
        getOptionLabel={(option) =>
          intl.formatMessage(option.description)
        }
        disableClearable={true}
        options={options}
        isOptionEqualToValue={(option, value) =>
          option.description === value.description
        }
        data-testid={'NotificationPosition:autocomplete'}
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              <FormattedMessage
                id='MyProfileDialog.NotificationPosition'
                defaultMessage='Notification position'
              />
            }
            InputProps={{...params.InputProps}}
          />
        )}
      />
      <Box sx={{paddingBottom: 3}} />
      <NotificationTab
        config={notificationConfiguration.info}
        type='info'
        onChange={(configuration: SeverityConfiguration, type: SeverityType) =>
          saveProfile(configuration, type)
        }
      />
      <Box sx={{paddingBottom: 2}} />
      <NotificationTab
        config={notificationConfiguration.success}
        type='success'
        onChange={(configuration: SeverityConfiguration, type: SeverityType) =>
          saveProfile(configuration, type)
        }
      />
      <Box sx={{paddingBottom: 2}} />
      <NotificationTab
        config={notificationConfiguration.warning}
        type='warning'
        onChange={(configuration: SeverityConfiguration, type: SeverityType) =>
          saveProfile(configuration, type)
        }
      />
      <Box sx={{paddingBottom: 2}} />
      <NotificationTab
        config={notificationConfiguration.error}
        type='error'
        onChange={(configuration: SeverityConfiguration, type: SeverityType) =>
          saveProfile(configuration, type)
        }
      />
    </Box>
  )
}

export default SnackBarTab
